import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "../auth/http.js";
import { useSession } from "../session/SessionContext.js";
import {
  DEMO_FLAG_KEYS,
  listDemoFlagAudit,
  resolveDemoFlags,
  setDemoFlag,
} from "./demo-flags.js";
import { queryKeys } from "./query-keys.js";

export { DEMO_FLAG_KEYS };

export function useFeatureFlags() {
  const { session, isDemoMode } = useSession();
  const tenantId = session?.companyId ?? "demo-tenant";

  return useQuery({
    queryKey: queryKeys.featureFlags.map(tenantId),
    staleTime: 15_000,
    queryFn: async () => {
      if (isDemoMode) return resolveDemoFlags(tenantId);
      return getApiClient().getFeatureFlags();
    },
  });
}

export function useFeatureFlag(key: string): boolean | undefined {
  const query = useFeatureFlags();
  return query.data?.flags[key];
}

export function useFeatureFlagAudit(key?: string) {
  const { isDemoMode } = useSession();

  return useQuery({
    queryKey: queryKeys.featureFlags.audit(key),
    staleTime: 5_000,
    queryFn: async () => {
      if (isDemoMode) return { data: listDemoFlagAudit(key) };
      return getApiClient().listFeatureFlagAudit(key);
    },
  });
}

export function useSetFeatureFlag() {
  const { session, isDemoMode } = useSession();
  const tenantId = session?.companyId ?? "demo-tenant";
  const actor = session?.email ?? "demo";
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: { key: string; enabled: boolean }) => {
      if (isDemoMode) {
        return setDemoFlag({
          tenantId,
          key: input.key,
          enabled: input.enabled,
          actor,
        });
      }
      return getApiClient().setFeatureFlag(input.key, {
        scope: "tenant",
        targetId: tenantId,
        enabled: input.enabled,
        actor,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.featureFlags.all });
    },
  });
}
